<template>
  <div
    v-if="!isIntegrationUnavailable"
    class="insights-meetings-widget insights-widget"
  >
    <InsightsMeetingsDialog
      v-if="showDialog && !isEmptyWidget"
      :show="showDialog"
      @close="toggleDialog"
    />

    <InsightsIntegrationFailureDialog
      v-if="showFailureDialog"
      :show="showFailureDialog"
      @close="toggleFailureDialog"
    />

    <div
      class="widget-wrapper d-flex justify-content-start"
      :class="{ 'blurred-bg': isReady && isStillSyncing }"
    >
      <div class="widget-common widget-small flex-1">
        <div class="widget-title d-flex align-items-center justify-space-between">
          <div>
            <span>{{ title }}</span>
            <i
              v-if="tooltip"
              v-tooltip="tooltipOptions"
              :title="tooltip"
              class="hi hi-info-filled ml-5"
            />
          </div>
          <a
            v-if="isIntegrationSynced && !isEmptyWidget"
            v-track:click="trackToggleProperties"
            class="insights-widget-view-more"
            @click.prevent="toggleDialog"
          >
            <i class="hi hi-external" />
            <span>More details</span>
          </a>
        </div>
        <div
          class="widget-body"
          :class="bodyClasses"
        >
          <div v-if="integrationExists && isReady">
            <div
              v-if="isReady || isIntegrationSyncing"
              class="insights-meetings-widget-content"
            >
              <template v-if="isIntegrationSynced">
                <template v-if="!isEmptyWidget">
                  <InsightsMeetingsTimeSpent
                    :chart-radius="chartRadius"
                    :chart-size="chartSize"
                    :with-legend="expanded"
                  />
                  <InsightsMeetingsRecurring
                    v-if="expanded"
                    :period="period"
                  />
                </template>
                <InsightsNoData
                  v-else
                  :type="emptyReason"
                />
              </template>

              <InsightsIntegrationSyncStatus
                v-else
                title="Synchronizing calendar data"
                :is-still-syncing="isStillSyncing"
              />
            </div>
          </div>
          <InsightsWidgetIntegration
            v-else-if="isReady && widgetData"
            show-illustration
            :widget-key="widgetKey"
            :widget-data="widgetData"
          >
            <template #description> Connect your team's calendars and boost Insights with meetings data</template>
          </InsightsWidgetIntegration>
        </div>
        <div
          v-if="showWidgetBenchmarks"
          class="widget-footer-bordered"
        >
          <InsightsBenchmarksAverages />
        </div>
      </div>
    </div>
  </div>

  <InsightsMeetingsPlaceholder
    v-else
    class="insight-widget-placeholder-meetings"
  />
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { INSIGHTS } from '~/app_constants';
import { mapGetters } from 'vuex';
import { Insights, Integrations, InsightsTracking } from '~/mixins';
import InsightsMeetingsDialog from '~/components/insights/InsightsMeetingsDialog.vue';
import InsightsMeetingsTimeSpent from '~/components/insights/InsightsMeetingsTimeSpent.vue';
import InsightsMeetingsRecurring from '~/components/insights/InsightsMeetingsRecurring.vue';
import InsightsWidgetIntegration from '~/components/insights/InsightsWidgetIntegration.vue';
import InsightsIntegrationFailureDialog from '~/components/insights/InsightsIntegrationFailureDialog.vue';
import InsightsIntegrationSyncStatus from '~/components/insights/InsightsIntegrationSyncStatus.vue';

export default {
  name: 'InsightsMeetingsWidget',
  components: {
    InsightsMeetingsDialog,
    InsightsMeetingsTimeSpent,
    InsightsMeetingsRecurring,
    InsightsWidgetIntegration,
    InsightsIntegrationFailureDialog,
    InsightsIntegrationSyncStatus,
    InsightsMeetingsPlaceholder: defineAsyncComponent(() => import('~/components/insights/InsightsMeetingsPlaceholder.vue')),
    InsightsBenchmarksAverages: defineAsyncComponent(() => import('~/components/insights/InsightsBenchmarksAverages.vue')),
    InsightsNoData: defineAsyncComponent(() => import('~/components/insights/InsightsNoData.vue')),
  },
  mixins: [Insights, Integrations, InsightsTracking],
  props: {
    chartSize: {
      type: Number,
      default: 90,
    },
    chartRadius: {
      type: Number,
      default: 38,
    },
    expanded: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    widgetKey: INSIGHTS.api.meetings,
    showDialog: false,
    retryFetch: INSIGHTS.meetings.retryFetch,
    tooltip:
      "We consider meetings based on your team’s calendar events that had more than 1 attendant. Meetings total time is calculated based on the calendar event's duration, and it is compared to the total work time of all participants.",
    tooltipOptions: Object.freeze({
      placement: 'top',
    }),
    title: 'Meetings',
  }),
  computed: {
    ...mapGetters({
      widgetData: 'meetings',
    }),
    ...mapGetters(['status', 'isDayTimePeriod', 'showBenchmarksAverages', 'showWidgetBenchmarks']),
    period() {
      return this.isDayTimePeriod ? 'day' : 'week';
    },
    integrationStatus() {
      return this.widgetData?.calendar_integration_status;
    },
    trackToggleProperties() {
      return Object.freeze({
        label: 'Widget click',
        widget_name: this.title,
        action_type: 'details',
        action_label: 'More details',
        ...this.commonProps(),
      });
    },
  },
  created() {
    this.fetch();
    this.checkFailureStatus();
  },
  methods: {
    toggleDialog() {
      this.showDialog = !this.showDialog;
    },
  },
};
</script>
